import { HTMLAttributes, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { MarketBanner, MarketButton } from '@market/react';
import {
  BUNDLE_OFFER_RESOLUTIONS,
  BUNDLE_SQUARE_BASE_PRODUCT_KEYS,
} from 'constants/software';
import { formatTierMonthlyPrice } from 'components/common/receipt/bundle_offer_utils';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useUpdateLineItemQuantities } from 'typescript/hooks/cart_client/use_update_line_item_quantities';
import { LineItemAttributes } from 'typescript/clients/solidshop/cart_client';
import { QUERY_KEYS } from 'typescript/constants/react_query';
import { trackCdp } from 'utilities/tracking';
import {
  BundleOfferTierKey,
  HardwareBundleOffer,
} from 'typescript/types/api/solidshop';

// Resolutions that surface a payment-step message. Other resolutions
// (plus / premium / upgrade_in_cart / suppress) are silent at payment — the
// cart's <BundleOfferCard> is their only surface. ineligible_to_migrate only
// renders when the reconciler actually removed a line (removed_tier present);
// without one it stays silent, tracked as a signed-in resolver outcome.
const MESSAGE_RESOLUTIONS = new Set<string>([
  BUNDLE_OFFER_RESOLUTIONS.ALREADY_SUBSCRIBED,
  BUNDLE_OFFER_RESOLUTIONS.UPGRADE_TO_PREMIUM,
  BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE,
]);

const BUNDLE_CDP_PAYMENT_STEP_SOURCE = 'payment_step';

const tierLabel = (
  offer: HardwareBundleOffer,
  tierKey: BundleOfferTierKey | null,
): string | null => (tierKey ? (offer.tiers[tierKey]?.label ?? null) : null);

/**
 * Surfaces, at the payment step, why the seller's cart looks the way it does
 * after the cart-merge reconciler ran on sign-in:
 *   - already_subscribed   — they already hold the recommended tier or higher
 *   - upgrade_to_premium   — they're on Plus, the hardware wants Premium, and
 *     no bundle line is in the cart yet
 *   - ineligible_to_migrate — they cannot migrate to Square One; when the
 *     reconciler removed a line (removed_tier present) a banner says so,
 *     otherwise tracked only
 *
 * Renders nothing for every other resolution (and when no payload exists, which
 * is the not-signed-in case — the resolver needs a merchant token to run).
 * upgrade_in_cart — the seller already added the higher-tier line — is
 * deliberately silent: the order summary lists the line, so there's nothing
 * to ask of them.
 */
const BundleReconciliationMessage = ({
  className = '',
}: HTMLAttributes<HTMLElement>) => {
  const { t } = useTranslation();
  const { orderNumber } = useCheckoutFlow();
  const migrationIneligibleTrackedRef = useRef(false);
  const upgradeOfferedTrackedRef = useRef(false);
  const queryClient = useQueryClient();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const { updateLineItemQuantities, isPending } = useUpdateLineItemQuantities();

  const offer = orderSummaryData?.hardware_bundle_offer;
  const experimentName = offer?.experiment_name;
  const experimentVariation = offer?.experiment_variation;

  useEffect(() => {
    if (
      offer?.resolution !== BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE ||
      migrationIneligibleTrackedRef.current
    ) {
      return;
    }

    migrationIneligibleTrackedRef.current = true;
    trackCdp({
      name: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_MIGRATION_INELIGIBLE_RESOLVED,
      params: {
        feature: 'shop',
        order_number: orderNumber,
        source: BUNDLE_CDP_PAYMENT_STEP_SOURCE,
        ...(experimentName && { experiment_name: experimentName }),
        ...(experimentVariation && {
          experiment_variation: experimentVariation,
        }),
      },
    });
  }, [experimentName, experimentVariation, offer?.resolution, orderNumber]);

  useEffect(() => {
    if (
      offer?.resolution !== BUNDLE_OFFER_RESOLUTIONS.UPGRADE_TO_PREMIUM ||
      upgradeOfferedTrackedRef.current
    ) {
      return;
    }

    upgradeOfferedTrackedRef.current = true;
    trackCdp({
      name: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_UPGRADE_OFFERED,
      params: {
        feature: 'shop',
        order_number: orderNumber,
        source: BUNDLE_CDP_PAYMENT_STEP_SOURCE,
        ...(experimentName && { experiment_name: experimentName }),
        ...(experimentVariation && {
          experiment_variation: experimentVariation,
        }),
        active_tier: offer.active_tier,
        offered_tier: offer.recommended_tier,
        variant_token: offer.tiers.premium?.variant_token,
      },
    });
  }, [
    offer?.active_tier,
    offer?.recommended_tier,
    offer?.resolution,
    offer?.tiers.premium?.variant_token,
    experimentName,
    experimentVariation,
    orderNumber,
  ]);

  if (!offer || !MESSAGE_RESOLUTIONS.has(offer.resolution)) {
    return null;
  }

  if (offer.resolution === BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE) {
    // removed_tier is only set when the reconciler stripped a line from
    // this order; a label can still be missing if the tiers payload lost
    // that tier — stay silent rather than render a broken sentence.
    const removedLabel = tierLabel(offer, offer.removed_tier);
    if (!removedLabel) {
      return null;
    }
    return (
      <MarketBanner className={className} variant="info">
        <p data-testid="bundle-reconciliation-removed">
          {t('cart.bundleOffer.removedFromOrder', { label: removedLabel })}
        </p>
      </MarketBanner>
    );
  }

  if (offer.resolution === BUNDLE_OFFER_RESOLUTIONS.ALREADY_SUBSCRIBED) {
    const label = tierLabel(offer, offer.active_tier);
    return (
      <MarketBanner className={className} variant="success">
        <p data-testid="bundle-reconciliation-already-subscribed">
          {t('cart.bundleOffer.alreadySubscribed', { label })}
        </p>
      </MarketBanner>
    );
  }

  // upgrade_to_premium: seller is on Plus and the hardware works best with
  // Premium. Compute the $/mo delta FE-side from the tier view-model and offer
  // a one-tap add of the Premium line. The resolver only returns this
  // resolution when no bundle line is in the cart, but the summary can be
  // stale, so the mutation overwrites: any bundle line already present is
  // zeroed in the same request rather than double-added.
  const plusTier = offer.tiers.plus;
  const premiumTier = offer.tiers.premium;
  const activeLabel = tierLabel(offer, offer.active_tier);
  const hasDelta =
    typeof plusTier?.monthly_cents === 'number' &&
    typeof premiumTier?.monthly_cents === 'number';
  const deltaPrice = hasDelta
    ? formatTierMonthlyPrice({
        monthly_cents: premiumTier.monthly_cents - plusTier.monthly_cents,
        currency: premiumTier.currency,
      })
    : null;

  const premiumVariantToken = premiumTier?.variant_token;

  const onUpgrade = () => {
    if (!premiumVariantToken) return;
    const lineItemsByVariantToken = new Map<string, LineItemAttributes>();
    (orderSummaryData?.cart_summary ?? [])
      .filter((item) =>
        BUNDLE_SQUARE_BASE_PRODUCT_KEYS.includes(item.base_product_key),
      )
      .forEach((item) => {
        lineItemsByVariantToken.set(item.product_variant.token, {
          variant_token: item.product_variant.token,
          quantity: 0,
        });
      });
    lineItemsByVariantToken.set(premiumVariantToken, {
      variant_token: premiumVariantToken,
      quantity: 1,
    });
    updateLineItemQuantities(
      {
        lineItems: [...lineItemsByVariantToken.values()],
        action: 'overwrite',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
          });
        },
      },
    );
  };

  return (
    <MarketBanner className={className} variant="info">
      <p data-testid="bundle-reconciliation-upgrade">
        {t('cart.bundleOffer.upgradeMessage', {
          label: activeLabel,
          price: deltaPrice,
        })}
      </p>
      {premiumVariantToken && (
        <MarketButton
          slot="action"
          rank="secondary"
          {...(isPending && { disabled: true })}
          onClick={onUpgrade}
        >
          {t('cart.bundleOffer.upgradeCta')}
        </MarketButton>
      )}
    </MarketBanner>
  );
};

export default BundleReconciliationMessage;
